import { filesize } from 'filesize';
import { matchesFileType } from './file-type';
import {
  createFileValidationContext,
  type FileValidationContext,
} from './file-validation-context';

export type FileValidationError = {
  code: string;
  title: string;
  message: string;
};

export type FileValidator = (
  context: FileValidationContext,
) => FileValidationError | null | Promise<FileValidationError | null>;

type Range = { min: number; max: number; exclusive?: boolean };
type ErrorOptions = { title?: string; message?: string };

function outsideRange(value: number, range: Range) {
  return (
    !Number.isFinite(value) ||
    (range.exclusive
      ? value <= range.min || value >= range.max
      : value < range.min || value > range.max)
  );
}

function describeRange(range: Range) {
  return range.exclusive
    ? `大于 ${range.min} 且小于 ${range.max}`
    : `不小于 ${range.min} 且不大于 ${range.max}`;
}

function compose(validators: readonly FileValidator[]): FileValidator {
  return async (context) => {
    for (const validator of validators) {
      const error = await validator(context);
      if (error) return error;
    }
    return null;
  };
}

function accept(format: string): FileValidator {
  return async ({ file, getMime }) => {
    const mime = await getMime();
    return matchesFileType(mime, format)
      ? null
      : {
          code: 'accept',
          title: '文件类型不支持',
          message: `文件「${file.name}」的类型为「${mime}」，不符合要求，请选择 ${format}`,
        };
  };
}

function maxSize(
  bytes: number,
  options: ErrorOptions & { exclusive?: boolean } = {},
): FileValidator {
  return ({ file }) =>
    (options.exclusive ? file.size >= bytes : file.size > bytes)
      ? {
          code: 'max-size',
          title: options.title ?? '文件大小超出限制',
          message: options.message ?? `文件大小不能超过 ${filesize(bytes)}`,
        }
      : null;
}

function whenMime(format: string, validator: FileValidator): FileValidator {
  return async (context) =>
    matchesFileType(await context.getMime(), format)
      ? validator(context)
      : null;
}

function duration(range: Range, options: ErrorOptions = {}): FileValidator {
  return async ({ getDuration }) => {
    const value = await getDuration();
    return outsideRange(value, range)
      ? {
          code: 'duration',
          title: options.title ?? '媒体时长不符合要求',
          message:
            options.message ??
            `媒体时长需${describeRange(range)}秒，当前为 ${value} 秒`,
        }
      : null;
  };
}

function dimensions(range: Range): FileValidator {
  return async ({ getDimensions }) => {
    const { width, height } = await getDimensions();
    return width <= 0 ||
      height <= 0 ||
      outsideRange(width, range) ||
      outsideRange(height, range)
      ? {
          code: 'dimensions',
          title: '媒体尺寸不符合要求',
          message: `宽高需${describeRange(range)}像素，当前为 ${width}x${height}`,
        }
      : null;
  };
}

function aspectRatio(range: Range): FileValidator {
  return async ({ getDimensions }) => {
    const { width, height } = await getDimensions();
    const value = width / height;
    return width <= 0 || height <= 0 || outsideRange(value, range)
      ? {
          code: 'aspect-ratio',
          title: '媒体宽高比不符合要求',
          message: `宽高比需${describeRange(range)}，当前为 ${value.toLocaleString()}`,
        }
      : null;
  };
}

function pixelCount(range: Range): FileValidator {
  return async ({ getDimensions }) => {
    const { width, height } = await getDimensions();
    const value = width * height;
    return width <= 0 || height <= 0 || outsideRange(value, range)
      ? {
          code: 'pixel-count',
          title: '媒体分辨率不符合要求',
          message: `总像素数需${describeRange(range)}，当前为 ${value.toLocaleString()} 像素`,
        }
      : null;
  };
}

function excludeCodecs(codecs: readonly string[]): FileValidator {
  return async ({ getVideoTrack }) => {
    const codec = await (await getVideoTrack()).getCodec();
    if (!codec) throw new Error('无法识别视频编码');
    return codecs.includes(codec)
      ? {
          code: 'codec',
          title: '视频编码不符合要求',
          message: `不支持当前视频编码格式（${codec}）`,
        }
      : null;
  };
}

function frameRate(range: Range): FileValidator {
  return async ({ getFrameRate }) => {
    const value = await getFrameRate();
    return value <= 0 || outsideRange(value, range)
      ? {
          code: 'frame-rate',
          title: '视频帧率不符合要求',
          message: `帧率需${describeRange(range)} FPS，当前为 ${value.toLocaleString()} FPS`,
        }
      : null;
  };
}

export const FileValidators = {
  compose,
  accept,
  maxSize,
  whenMime,
  duration,
  dimensions,
  aspectRatio,
  pixelCount,
  excludeCodecs,
  frameRate,
};

export async function validateFile(
  file: File,
  validator: FileValidator,
): Promise<FileValidationError | null> {
  const context = createFileValidationContext(file);
  try {
    return await validator(context);
  } catch {
    return {
      code: 'file-read',
      title: '文件校验失败',
      message: `无法读取文件「${file.name}」的有效信息，请重新选择`,
    };
  } finally {
    context.dispose();
  }
}
